import React from 'react'
import { Link } from 'react-router-dom'

const Section: React.FC<{ id?: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed">{children}</div>
  </section>
)

export const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen pt-16">
      <section className="py-16 bg-gradient-to-br from-kylix-primary via-purple-800 to-pink-800 text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-4">Termos de Uso</h1>
            <p className="text-gray-200">Última atualização: 24 de setembro de 2026</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <Section title="1. Quem somos e a quem se aplica">
              <p>
                Estes termos regem o uso do site <strong>smartdataerp.com.br</strong> e da plataforma SmartData
                (incluindo o Emissor Direto), oferecidos pela <strong>FOCO DESENVOLVIMENTO TECNOLOGICO LTDA</strong>,
                CNPJ <strong>10.344.306/0001-19</strong>, com sede em Sorocaba/SP ("SmartData", "nós").
              </p>
              <p>
                Aplicam-se às empresas que contratam a plataforma ("empresas clientes") e às pessoas que as
                representam. Ao criar uma conta ou usar a plataforma, a empresa cliente concorda com estes termos.
              </p>
            </Section>

            <Section title="2. O serviço">
              <p>
                A plataforma é um sistema de gestão empresarial na nuvem que permite, entre outras funções,
                cadastrar produtos, clientes e fornecedores, registrar vendas e orçamentos, controlar contas a
                receber e emitir documentos fiscais eletrônicos (NF-e, NFC-e e NFS-e).
              </p>
              <p>
                A plataforma também envia, em nome da empresa cliente, mensagens transacionais pelo WhatsApp
                Business Platform (Meta) e por e-mail, como a disponibilização de documento fiscal, o envio de
                orçamento e lembretes de parcela em aberto.
              </p>
            </Section>

            <Section title="3. Conta e acesso">
              <ul className="list-disc pl-6 space-y-2">
                <li>A empresa cliente deve fornecer informações verdadeiras e mantê-las atualizadas.</li>
                <li>As credenciais de acesso são pessoais; a empresa cliente responde pelo uso da sua conta.</li>
                <li>Suspeita de acesso indevido deve ser comunicada imediatamente a contato@smartdataerp.com.br.</li>
              </ul>
            </Section>

            <Section title="4. Responsabilidades da empresa cliente">
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  Conferir os dados fiscais (produtos, tributação, destinatários) e os documentos emitidos, que são
                  de sua responsabilidade perante o Fisco, assim como o certificado digital utilizado.
                </li>
                <li>
                  Cadastrar somente clientes e fornecedores com quem tenha relação comercial e ter base legal para
                  tratar seus dados, nos termos da LGPD, atuando como controladora desses dados.
                </li>
                <li>
                  Enviar mensagens apenas a pessoas que esperam recebê-las em razão dessa relação (documentos,
                  orçamentos e cobranças), respeitando as políticas do WhatsApp Business e pedidos para não receber
                  mais mensagens. É proibido usar a plataforma para spam, marketing não solicitado ou conteúdo
                  ilícito.
                </li>
                <li>Não tentar acessar dados de outras empresas clientes nem interferir no funcionamento da plataforma.</li>
              </ul>
            </Section>

            <Section title="5. Serviços de terceiros">
              <p>
                Algumas funções dependem de serviços de terceiros, como as Secretarias da Fazenda e prefeituras
                (autorização de documentos fiscais) e a Meta (WhatsApp Business Platform). A disponibilidade e as
                regras desses serviços não são controladas pela SmartData. Quando a empresa cliente conectar a sua
                própria conta do WhatsApp Business, as tarifas cobradas pela Meta por mensagem são de sua
                responsabilidade, conforme a tabela vigente da Meta.
              </p>
            </Section>

            <Section title="6. Planos e pagamento">
              <p>
                O uso da plataforma é remunerado conforme o plano contratado. Preços, forma de pagamento e
                reajustes constam da proposta ou da página de contratação. A falta de pagamento pode levar à
                suspensão do acesso, após aviso prévio.
              </p>
            </Section>

            <Section title="7. Disponibilidade e suporte">
              <p>
                Empregamos esforços razoáveis para manter a plataforma disponível e segura, mas podem ocorrer
                interrupções para manutenção ou por falhas de terceiros. Manutenções programadas serão
                comunicadas com antecedência sempre que possível.
              </p>
            </Section>

            <Section title="8. Dados e privacidade">
              <p>
                O tratamento de dados pessoais segue a nossa{' '}
                <Link to="/privacidade" className="text-kylix-secondary underline">Política de Privacidade</Link>.
                Os dados cadastrados pela empresa cliente pertencem a ela; a SmartData os trata apenas para prestar
                o serviço. Ao final do contrato, a empresa cliente pode solicitar a exportação dos seus dados, que
                serão excluídos após o prazo de guarda exigido por lei.
              </p>
            </Section>

            <Section title="9. Propriedade intelectual">
              <p>
                A plataforma, sua marca e seu código são da SmartData. A contratação concede à empresa cliente
                uma licença de uso, não exclusiva e intransferível, enquanto durar o contrato.
              </p>
            </Section>

            <Section title="10. Limitação de responsabilidade">
              <p>
                A SmartData não responde por danos decorrentes de dados incorretos informados pela empresa
                cliente, de decisões fiscais tomadas por ela, de indisponibilidade de serviços de terceiros ou de
                uso em desacordo com estes termos. Nos demais casos, a responsabilidade fica limitada ao valor
                pago pela empresa cliente nos 12 meses anteriores ao evento.
              </p>
            </Section>

            <Section title="11. Encerramento">
              <p>
                A empresa cliente pode encerrar o uso a qualquer momento. A SmartData pode suspender ou encerrar
                contas que violem estes termos, em especial o uso para envio de mensagens não solicitadas.
              </p>
            </Section>

            <Section title="12. Alterações e foro">
              <p>
                Estes termos podem ser atualizados; a data da última atualização aparece no topo desta página.
                Aplica-se a legislação brasileira, e fica eleito o foro da comarca de Sorocaba/SP.
              </p>
              <p>
                Contato:{' '}
                <a href="mailto:contato@smartdataerp.com.br" className="text-kylix-secondary underline">
                  contato@smartdataerp.com.br
                </a>
                .
              </p>
            </Section>
          </div>
        </div>
      </section>
    </div>
  )
}
